import {
  Breadcrumb,
  Button,
  Col,
  FormControl,
  FormGroup,
  InputGroup,
  Modal,
  Placeholder,
  Row,
  Table,
} from "react-bootstrap";
import { AccountBreadcrumbs, WithAccount } from "../util.js";
import {
  Check,
  PencilFill,
  Key,
  Trash,
  CheckCircle,
  XCircle,
} from "react-bootstrap-icons";
import { Suspense, useCallback, useEffect, useState } from "react";
import { Await, useFetcher, useLoaderData, useNavigation } from "react-router";
import { CollectorCredential } from "../ApiClient.js";
import React from "react";
import { DateTime } from "luxon";
import CollectorCredentialForm from "./CollectorCredentialForm.js";

export default function CollectorCredentials() {
  return (
    <>
      <Breadcrumbs />
      <Row>
        <Col>
          <h1>
            <Key />{" "}
            <Suspense fallback={<Placeholder animation="glow" xs={6} />}>
              <WithAccount>{(account) => account.name}</WithAccount>
            </Suspense>{" "}
            Collector Credentials
          </h1>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <CollectorCredentialForm />
        </Col>
      </Row>
      <Row>
        <Col>
          <CollectorCredentialList />
        </Col>
      </Row>
    </>
  );
}

function Breadcrumbs() {
  return (
    <AccountBreadcrumbs>
      <Breadcrumb.Item active>Collector Credentials</Breadcrumb.Item>
    </AccountBreadcrumbs>
  );
}

function CollectorCredentialList() {
  const { collectorCredentials } = useLoaderData() as {
    collectorCredentials: Promise<CollectorCredential[]>;
  };

  return (
    <Table>
      <thead>
        <tr>
          <td>Name</td>
          <td>KEM</td>
          <td>KDF</td>
          <td>AEAD</td>
          <td>Has token</td>
          <td>Created</td>
          <td></td>
        </tr>
      </thead>
      <tbody>
        <Suspense>
          <Await resolve={collectorCredentials}>
            {(collectorCredentials: CollectorCredential[]) =>
              collectorCredentials.map((collectorCredential) => (
                <CollectorCredentialRow
                  key={collectorCredential.id}
                  collectorCredential={collectorCredential}
                />
              ))
            }
          </Await>
        </Suspense>
      </tbody>
    </Table>
  );
}

function Name({
  collectorCredential,
}: {
  collectorCredential: CollectorCredential;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const edit = useCallback(() => setIsEditing(true), [setIsEditing]);
  const fetcher = useFetcher();
  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    if (fetcher.data) setIsEditing(false);
  }, [fetcher, setIsEditing]);
  if (isEditing) {
    return (
      <fetcher.Form action={collectorCredential.id} method="PATCH">
        <FormGroup>
          <InputGroup>
            <FormControl
              type="text"
              name="name"
              defaultValue={collectorCredential.name || ""}
              data-1p-ignore
              autoFocus
            />
            <Button type="submit">
              <Check />
            </Button>
          </InputGroup>
        </FormGroup>
      </fetcher.Form>
    );
  } else {
    return (
      <span onClick={edit}>
        {collectorCredential.name ||
          `Collector Credential ${collectorCredential.hpke_config.id}`}{" "}
        <Button
          variant="outline-secondary"
          onClick={edit}
          size="sm"
          className="ml-auto"
        >
          <PencilFill />
        </Button>
      </span>
    );
  }
}

function RelativeTime({ time, missing }: { time?: string; missing?: string }) {
  return time ? (
    <relative-time datetime={time} format="relative">
      {DateTime.fromISO(time).toLocal().toLocaleString(DateTime.DATETIME_SHORT)}
    </relative-time>
  ) : (
    <>{missing || "never"}</>
  );
}

function DeleteButton({
  collectorCredential,
}: {
  collectorCredential: CollectorCredential;
}) {
  const navigation = useNavigation();

  const [show, setShow] = useState(false);
  const close = React.useCallback(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setShow(false);
  }, []);
  const open = React.useCallback(() => setShow(true), []);
  const fetcher = useFetcher();

  useEffect(() => {
    if (fetcher.data) close();
  }, [fetcher, close]);

  return (
    <>
      <Button
        variant="outline-danger"
        className="ml-auto"
        size="sm"
        onClick={open}
      >
        <Trash />
      </Button>
      <Modal show={show} onHide={close}>
        <Modal.Header closeButton>
          <Modal.Title>
            Confirm Collector Credential Deletion {collectorCredential.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This collector credential will immediately be inactivated and cannot
          be used to create new tasks. Existing tasks will continue to function.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            Close
          </Button>
          <fetcher.Form method="DELETE" action={collectorCredential.id}>
            <Button
              variant="danger"
              type="submit"
              disabled={navigation.state === "submitting"}
            >
              <Trash /> Delete
            </Button>
          </fetcher.Form>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function CollectorCredentialRow({
  collectorCredential,
}: {
  collectorCredential: CollectorCredential;
}) {
  return (
    <tr>
      <td>
        <Name collectorCredential={collectorCredential} />
      </td>
      <td>{collectorCredential.hpke_config.kem_id}</td>
      <td>{collectorCredential.hpke_config.kdf_id}</td>
      <td>{collectorCredential.hpke_config.aead_id}</td>
      <td>{collectorCredential.token_hash ? <CheckCircle /> : <XCircle />}</td>
      <td>
        <RelativeTime time={collectorCredential.created_at} />
      </td>
      <td>
        <DeleteButton collectorCredential={collectorCredential} />
      </td>
    </tr>
  );
}
